import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getMynewsdeskItemBySlug } from "@/lib/mynewsdesk";
import { sanitizeBlogHtml } from "@/lib/sanitizeHtml";

const LOCALE_MAP: Record<string, string> = { en: "en-US", sv: "sv-SE", ja: "ja-JP", zh: "zh-CN" };

const PressRelease = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation("blog");
  const dateLocale = LOCALE_MAP[i18n.language] || "en-US";

  const { data: release, isLoading, isError } = useQuery({
    queryKey: ["press-release", slug],
    enabled: !!slug,
    staleTime: 10 * 60 * 1000,
    queryFn: () => getMynewsdeskItemBySlug(slug as string),
  });

  // Some Mynewsdesk items have no summary: fall back to the first text of the body
  // so social crawlers never receive an empty description.
  const metaDescription = (() => {
    if (!release) return "Press release from Cetasol.";
    if (release.summary?.trim()) return release.summary.trim().slice(0, 155);
    const text = (release.body || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    return (text || "Press release from Cetasol.").slice(0, 155);
  })();

  const formatDate = (date: string) =>
    date ? new Date(date).toLocaleDateString(dateLocale, { year: "numeric", month: "long", day: "numeric" }) : "";

  const backTarget =
    release?.typeOfMedia === "news"
      ? { path: "/news", en: "Back to news", sv: "Tillbaka till nyheter" }
      : release?.typeOfMedia === "blog_post"
        ? { path: "/insight/blog-post", en: "Back to blog posts", sv: "Tillbaka till blogginlägg" }
        : { path: "/insight/press-release", en: "Back to press releases", sv: "Tillbaka till pressreleaser" };

  const backLink = (
    <Link
      to={backTarget.path}
      className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
    >
      <ArrowLeft size={16} /> {i18n.language === "sv" ? backTarget.sv : backTarget.en}
    </Link>
  );

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{release ? `${release.title.slice(0, 55)} | Cetasol` : "Press release | Cetasol"}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`https://cetasol.com/press/${slug}`} />
        <meta property="og:title" content={release?.title || "Press release | Cetasol"} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://cetasol.com/press/${slug}`} />
        <meta property="og:image" content={release?.image || "https://cetasol.com/og-image.jpg"} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={release?.title || "Press release | Cetasol"} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={release?.image || "https://cetasol.com/og-image.jpg"} />
      </Helmet>
      <Header />
      <main className="min-h-screen pt-24 pb-16 section-fade">
        <div className="container mx-auto px-6 max-w-3xl">
          {backLink}

          {isLoading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-card w-1/4" />
              <div className="h-10 bg-card w-3/4" />
              <div className="aspect-[16/9] bg-card" />
              <div className="h-3 bg-card w-full" />
              <div className="h-3 bg-card w-5/6" />
            </div>
          ) : isError || !release ? (
            <div className="py-16 text-center">
              <h1 className="text-2xl font-bold mb-4">
                {i18n.language === "sv" ? "Pressreleasen kunde inte hittas" : "Press release not found"}
              </h1>
              <Link to="/insight/press-release" className="text-primary hover:underline">
                {i18n.language === "sv" ? "Se alla pressreleaser" : "See all press releases"}
              </Link>
            </div>
          ) : (
            <article>
              <p className="text-sm text-muted-foreground mb-3">{formatDate(release.publishedAt)}</p>
              <h1 className="text-3xl md:text-4xl font-bold mb-8 leading-tight">{release.title}</h1>

              {release.image && (
                <img
                  src={release.image}
                  alt={release.title}
                  className="w-full aspect-[16/9] object-cover mb-8"
                  loading="lazy"
                />
              )}

              {release.summary && (
                <p className="text-lg text-foreground/90 leading-relaxed mb-8 font-medium">{release.summary}</p>
              )}

              <div
                className="prose prose-invert prose-lg max-w-none
                  prose-headings:text-foreground
                  prose-h2:text-2xl prose-h2:font-bold prose-h2:mt-12 prose-h2:mb-4
                  prose-h3:text-xl prose-h3:font-semibold prose-h3:mt-10 prose-h3:mb-3
                  prose-p:text-foreground/80 prose-p:leading-relaxed prose-p:mb-4
                  prose-a:text-primary prose-a:underline prose-a:underline-offset-4
                  prose-strong:text-foreground prose-strong:font-semibold
                  prose-ul:my-4 prose-ul:space-y-1.5 prose-ol:my-4 prose-ol:space-y-1.5
                  prose-li:text-foreground/80 prose-li:leading-relaxed
                  prose-img:mx-auto prose-img:my-8
                  prose-hr:border-border prose-hr:my-10"
                dangerouslySetInnerHTML={{ __html: sanitizeBlogHtml(release.body || "") }}
              />

              {release.boilerplate && (
                <div
                  className="mt-12 pt-8 border-t border-border prose prose-invert max-w-none prose-p:text-muted-foreground prose-p:text-sm prose-strong:text-foreground"
                  dangerouslySetInnerHTML={{ __html: sanitizeBlogHtml(release.boilerplate) }}
                />
              )}

              <div className="mt-12">{backLink}</div>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default PressRelease;
